import React, { useState, useRef, useEffect } from 'react';
import { Send, X, MessageSquare, ArrowDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const ChatPanel = ({ 
  chatMessages, 
  onSendMessage, 
  isMobile, 
  showChatLog,
  setShowChatLog,
  isFullscreen,
  hideFullscreenControls,
  currentRoom,
  typingPlayers,
  onTypingStart,
  onTypingStop,
  isGuest,
  overlayMode = false
}) => {
  const [chatInput, setChatInput] = useState('');
  const chatContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  // City/apartments: chat starts collapsed so it doesn't block the view
  const [overlayChatOpen, setOverlayChatOpen] = useState(() => {
    try { return localStorage.getItem('overlay_chat_open') === '1'; } catch { return false; }
  });
  const seenCountRef = useRef(0);
  const setOverlayOpen = (v) => {
    setOverlayChatOpen(v);
    try { localStorage.setItem('overlay_chat_open', v ? '1' : '0'); } catch {}
  };
  useEffect(() => {
    if (!overlayMode || overlayChatOpen) seenCountRef.current = chatMessages.length;
  }, [chatMessages, overlayMode, overlayChatOpen]);
  
  // Auto-scroll only when near the bottom; otherwise count unseen for the chip
  const prevChatCountRef = useRef(0);
  const [logUnseen, setLogUnseen] = useState(0);
  useEffect(() => {
    const box = chatContainerRef.current;
    if (!box) return;
    const added = chatMessages.length - prevChatCountRef.current;
    prevChatCountRef.current = chatMessages.length;
    const nearBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 60;
    if (nearBottom) {
      box.scrollTop = box.scrollHeight;
      setLogUnseen(0);
    } else if (added > 0) {
      setLogUnseen((c) => c + added);
    }
  }, [chatMessages]);

  const jumpToLatest = () => {
    const box = chatContainerRef.current;
    if (box) box.scrollTop = box.scrollHeight;
    setLogUnseen(0);
  };
  
  // Clear stale typing indicators (auto-clear after 3 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      Object.entries(typingPlayers || {}).forEach(([id, data]) => {
        if (now - data.timestamp > 3000) {
          // Will be cleared by parent via socket event
        }
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [typingPlayers]);
  
  const handleInputChange = (e) => {
    const value = e.target.value;
    setChatInput(value);
    
    // Typing indicator logic
    if (value.length > 0 && !isTypingRef.current) {
      isTypingRef.current = true;
      onTypingStart?.();
    }
    
    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      if (isTypingRef.current) {
        isTypingRef.current = false;
        onTypingStop?.();
      }
    }, 2000);
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    
    // Stop typing indicator when message is sent
    if (isTypingRef.current) {
      isTypingRef.current = false;
      onTypingStop?.();
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    onSendMessage(chatInput.trim());
    setChatInput('');
  };
  
  // Get list of typing players
  const typingList = Object.values(typingPlayers || {}).map(p => p.name);
  const typingText = typingList.length === 1 
    ? `${typingList[0]} is typing...`
    : typingList.length === 2
    ? `${typingList[0]} and ${typingList[1]} are typing...`
    : typingList.length > 2
    ? `${typingList[0]} and ${typingList.length - 1} others are typing...`
    : null;

  // In city/apartments the chat is collapsible so it doesn't block the view
  if (overlayMode && !overlayChatOpen) {
    const hasUnseen = chatMessages.length > seenCountRef.current;
    return (
      <button
        data-testid="chat-show-btn"
        onClick={() => { seenCountRef.current = chatMessages.length; setOverlayOpen(true); }}
        className={`fixed z-[70] ${isMobile ? 'bottom-16 right-2' : 'bottom-8 right-6'} w-11 h-11 rounded-full bg-black/70 hover:bg-black/90 border border-white/20 text-white flex items-center justify-center shadow-lg transition-colors`}
        title="Open chat"
      >
        <MessageSquare size={18} />
        {hasUnseen && (
          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-red-500 animate-pulse" data-testid="chat-unseen-dot" />
        )}
      </button>
    );
  }

  return (
    <>
      {/* Chat Log Panel - Shows recent messages */}
      {showChatLog && (
        <div
          className={`${overlayMode ? 'fixed z-[70]' : 'absolute'} ${overlayMode
            ? (isMobile ? 'bottom-32 left-2 right-16' : 'bottom-24 left-4 max-w-xs')
            : (isMobile ? 'bottom-14 left-2 right-16' : 'bottom-16 left-4 max-w-xs')}`}
        >
        <div 
          ref={chatContainerRef}
          onScroll={(e) => {
            const box = e.currentTarget;
            if (box.scrollHeight - box.scrollTop - box.clientHeight < 30) setLogUnseen(0);
          }}
          className={`${overlayMode
            ? (isMobile ? 'max-h-28' : 'max-h-40')
            : (isMobile ? 'max-h-24' : 'max-h-32')} overflow-y-auto bg-black/50 backdrop-blur rounded-lg p-2 space-y-1`}
          style={{ scrollbarWidth: 'thin' }}
          data-testid="chat-log-panel"
        >
          {chatMessages.slice(-8).map((msg, idx) => (
            <div key={idx} className="text-xs flex items-start gap-1">
              {/* Role badge */}
              {msg.role === 'owner' && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-amber-500/30 text-amber-300 text-[8px] font-bold flex-shrink-0 mt-0.5" title="Owner">&#x1F451;</span>
              )}
              {(msg.role === 'admin' || msg.role === 'overseer') && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-red-500/30 text-red-300 text-[8px] font-bold flex-shrink-0 mt-0.5" title={msg.role}>&#x26A1;</span>
              )}
              {msg.role === 'artist' && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-purple-500/30 text-purple-300 text-[8px] font-bold flex-shrink-0 mt-0.5" title="Artist">&#x1F3B5;</span>
              )}
              {msg.role === 'vip' && (
                <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-blue-500/30 text-blue-300 text-[8px] font-bold flex-shrink-0 mt-0.5" title="VIP">&#x1F48E;</span>
              )}
              <span>
                <span 
                  className={`font-bold ${
                    msg.role === 'owner' ? 'text-amber-400' : 
                    msg.role === 'admin' || msg.role === 'overseer' ? 'text-red-400' : 
                    msg.role === 'artist' ? 'text-purple-400' :
                    msg.role === 'vip' ? 'text-blue-400' :
                    'text-cyan-400'
                  }`}
                >
                  {msg.playerName}:
                </span>
                <span className="text-white/90 ml-1">{msg.text}</span>
              </span>
            </div>
          ))}
          {chatMessages.length === 0 && (
            <div className="text-white/40 text-xs text-center">No messages yet - say hi!</div>
          )}
          {/* Typing indicator */}
          {typingText && (
            <div className="text-xs text-purple-300/70 italic flex items-center gap-1">
              <span className="flex gap-0.5">
                <span className="w-1 h-1 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-1 h-1 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-1 h-1 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </span>
              {typingText}
            </div>
          )}
        </div>
        {logUnseen > 0 && (
          <button
            type="button"
            onClick={jumpToLatest}
            data-testid="new-messages-chip"
            className="absolute bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-purple-600 hover:bg-purple-500 text-white text-[10px] font-semibold px-2.5 py-1 rounded-full shadow-lg animate-pulse"
          >
            <ArrowDown size={11} /> {logUnseen} new
          </button>
        )}
        </div>
      )}
      
      {/* Chat Toggle Button */}
      <Button
        onClick={() => setShowChatLog(!showChatLog)}
        variant="ghost"
        size="icon"
        className={`${overlayMode ? 'fixed z-[70]' : 'absolute'} ${overlayMode
          ? (isMobile ? 'bottom-32 right-2' : 'bottom-24 right-4')
          : (isMobile ? 'bottom-14 right-2' : 'bottom-16 right-4')} bg-black/60 hover:bg-black/80 text-white rounded-full`}
        data-testid="chat-toggle-btn"
      >
        {showChatLog ? <X className="w-4 h-4" /> : <Send className="w-4 h-4" />}
      </Button>
      
      {/* Chat input - hideable in fullscreen */}
      {(!isFullscreen || !hideFullscreenControls) && (
        <form onSubmit={handleSubmit} className={`${overlayMode ? 'fixed z-[70]' : 'absolute'} ${overlayMode
          ? (isMobile ? 'bottom-16 left-2 right-2' : 'bottom-8 left-1/2 -translate-x-1/2 w-full max-w-md')
          : (isMobile ? 'bottom-2 left-2 right-2' : 'bottom-4 left-4 right-4')} flex gap-2`}>
          {overlayMode && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              data-testid="chat-hide-btn"
              onClick={() => { seenCountRef.current = chatMessages.length; setOverlayOpen(false); }}
              className="bg-black/60 hover:bg-black/80 text-white/70 hover:text-white rounded-full flex-shrink-0"
              title="Hide chat"
            >
              <X size={16} />
            </Button>
          )}
          <Input
            value={isGuest ? '' : chatInput}
            onChange={handleInputChange}
            placeholder={isGuest ? "Sign in to chat..." : overlayMode ? "Chat with people nearby..." : "Type a message..."}
            className={`bg-black/70 border-white/20 text-white placeholder:text-white/40 ${isMobile ? 'text-sm h-10' : 'text-base'} ${isGuest ? 'opacity-60 cursor-not-allowed' : ''}`}
            maxLength={50}
            data-testid="chat-input"
            disabled={isGuest}
          />
          <Button type="submit" size={isMobile ? 'sm' : 'icon'} className="bg-purple-600 hover:bg-purple-500 h-10 w-10" data-testid="chat-send-btn" disabled={isGuest}>
            <Send className="w-4 h-4" />
          </Button>
        </form>
      )}
    </>
  );
};

export default ChatPanel;
